/**
 * svgUtils — DOM helpers for SVG manipulation.
 */

/**
 * Detects UTF-16 LE BOM (`0xFF 0xFE`) and decodes accordingly.
 * All other buffers are treated as UTF-8.
 *
 * SVG files produced by the bim-cadaplus generator may be saved as UTF-16 LE
 * by some CAD tools — this handles that transparently.
 *
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
export function decodeSvgBuffer(buffer) {
  const bytes = new Uint8Array(buffer.slice(0, 2))
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(buffer)
  }
  return new TextDecoder('utf-8').decode(buffer)
}


/**
 * Forces an SVG element to fill its container by setting `height` and `width`
 * to `100%`. Overrides any hardcoded pixel dimensions baked into the SVG file.
 *
 * @param {SVGSVGElement} svgElement
 */
export function appendSvgHeightWidth(svgElement) {
  svgElement.setAttribute('height', '100%')
  svgElement.setAttribute('width', '100%')
}

/**
 * Injects or replaces the CSS content inside the `<style>` element of an SVG.
 *
 * - When the SVG already contains a `<style>` element its `innerHTML` is replaced.
 * - When no `<style>` is present a new one is created (SVG namespace) and appended.
 * - Passing an empty string / falsy `cssText` clears any existing style without
 *   adding a new element.
 *
 * @param {SVGSVGElement} svgElement
 * @param {string}        cssText
 */
export function appendSvgStyle(svgElement, cssText) {
  const existing = svgElement.querySelector('style')

  if (!cssText) {
    if (existing) existing.innerHTML = ''
    return
  }

  if (existing) {
    existing.innerHTML = cssText
  } else {
    const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style')
    styleEl.innerHTML = cssText
    svgElement.appendChild(styleEl)
  }
}

/**
 * Parses SVG path data into an array of absolute `[x, y]` coordinate pairs.
 *
 * Supports M/m (move-to), L/l (line-to), H/h (horizontal line-to),
 * V/v (vertical line-to) and Z/z (close path). Compound coordinates after M/m
 * are treated as implicit L/l pairs as per the SVG spec.
 *
 * Does not handle curves (C/c/Q/q/A/a) — those commands yield no points.
 * Sufficient for the simple polygonal compartment and window paths emitted
 * by the generator.
 *
 * @param {string} pathData  — value of an SVG path `d` attribute
 * @returns {[number, number][]}  — absolute [x, y] points; empty array for falsy input
 */
export function getPathsBoundaryPoints(pathData) {
  if (!pathData) return []
  const commands = pathData.match(/[a-zA-Z][^a-zA-Z]*/g)
  if (!commands) return []

  const points = []
  let cx = 0
  let cy = 0

  for (const command of commands) {
    const type = command[0]
    const tail = command.slice(1).trim()
    const values = tail === '' ? [] : tail.split(/[\s,]+/).map(Number)

    switch (type) {
      case 'M':
        if (values.length >= 2) {
          cx = values[0]; cy = values[1]
          points.push([cx, cy])
          for (let i = 2; i + 1 < values.length; i += 2) {
            cx = values[i]; cy = values[i + 1]
            points.push([cx, cy])
          }
        }
        break
      case 'm':
        if (values.length >= 2) {
          cx += values[0]; cy += values[1]
          points.push([cx, cy])
          for (let i = 2; i + 1 < values.length; i += 2) {
            cx += values[i]; cy += values[i + 1]
            points.push([cx, cy])
          }
        }
        break
      case 'L':
        for (let i = 0; i + 1 < values.length; i += 2) {
          cx = values[i]; cy = values[i + 1]
          points.push([cx, cy])
        }
        break
      case 'l':
        for (let i = 0; i + 1 < values.length; i += 2) {
          cx += values[i]; cy += values[i + 1]
          points.push([cx, cy])
        }
        break
      case 'H':
        for (const v of values) { cx = v; points.push([cx, cy]) }
        break
      case 'h':
        for (const v of values) { cx += v; points.push([cx, cy]) }
        break
      case 'V':
        for (const v of values) { cy = v; points.push([cx, cy]) }
        break
      case 'v':
        for (const v of values) { cy += v; points.push([cx, cy]) }
        break
      // Z/z — close path; no new vertex (start point already captured at M)
      default:
        break
    }
  }

  return points
}
